import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Segment } from 'src/app/models/services/segment';
import { SegmentStatus } from 'src/app/models/services/segmentStatus.enum';
import { SegmentsService } from 'src/app/services/segments.service';
import { Subscription, timer } from 'rxjs';
import { DialogService } from 'src/app/services/core/dialog.service';
import { ErisTableDataSource } from 'src/app/models/components/eris-table';
import { LookAlikesService } from 'src/app/services/look-alikes.service';
import { LookAlikeSourceTypes } from 'src/app/models/services/lookAlikeSourceTypes.enum';
import { BaseTenantRequiredPageComponent } from 'src/app/components/core/base-tenant-required-page/base-tenant-required-page.component';
import { GlobalsService } from 'src/app/services/globals.service';
import { UsersService } from 'src/app/services/users.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { AudienceParametersDialogService } from 'src/app/services/dialogs/audience-parameters-dialog.service';
import { AudienceParameters } from 'src/app/models/services/audience';
import { CreateAudienceRequest } from 'src/app/models/services/createAudience';
import { AudiencesService } from 'src/app/services/audiences/audiences.service';
import { MenuAction, MenuDivider } from 'src/app/components/core/eris-menu/eris-menu.component';
import { MenuCloseReason } from '@angular/material/menu/menu';

@Component({
  selector: 'app-segments-list',
  templateUrl: './segments-list.component.html',
  styleUrls: ['./segments-list.component.sass']
})
export class SegmentsListComponent extends BaseTenantRequiredPageComponent implements OnInit, OnDestroy {
  public heading: string = 'Segments list';
  public subheading: string = '';
  public icon: string = 'pe-7s-copy-file icon-gradient bg-midnight-bloom';

  private originalSegments: Segment[] = [];
  private activePolling: boolean = false;
  private loadSegmentsSubscription?: Subscription;

  public segmentsDataSource: ErisTableDataSource = {
    tableDefinition: [
      {
        headerDescription: 'Segment',
        enableSort: true,
        sortFieldName: 'fileName'
      }, {
        headerDescription: 'Created',
        enableSort: true,
        sortFieldName: 'createdOn'      
      }, {
        headerDescription: 'Identifiers uploaded',
        enableSort: true,
        sortFieldName: 'uploadedRecords'      
      }, {
        headerDescription: 'Matched profiles',
        enableSort: true,
        sortFieldName: 'matchedRecords'      
      }, {
        headerDescription: '',
        enableSort: false      
      }, {
        headerDescription: '',
        enableSort: false      
      }       
    ],
    data: [],
    enableSort: true,
    enablePagination: true,
    pageSize: 10
  };
  public segmentsFilter: string = '';
  public menuConfiguration: (MenuAction | MenuDivider)[] = [
    {
      label: "Refresh insights",
      iconClass: "pe-7s-refresh-2",
      action: (context: any) => this.calculateInsights(context),
      checkDisabled: (context: any): boolean => context?.status != 4,
      checkVisible: (context: any): boolean => !!context?.segmentInsightsCalculated,
    },
    {
      label: "Calculate insights",
      iconClass: "pe-7s-calculator",
      action: (context: any) => this.calculateInsights(context),
      checkDisabled: (context: any): boolean => context?.status != 4,
      checkVisible: (context: any): boolean => !context?.segmentInsightsCalculated,
    },
    {
      label: "View segment insights",
      iconClass: "pe-7s-graph3",
      action: (context: any) => this.viewInsights(context),
      checkDisabled: (context: any): boolean => context?.status != 4,
      checkVisible: (context: any): boolean => !!context?.segmentInsightsCalculated
    },
    {
      label: "Calculate look alikes parameters",
      iconClass: "pe-7s-power",
      action: (context: any) => this.calculateLookAlikesParameters(context),
      checkDisabled: (context: any): boolean => context?.status != 4,
      checkVisible: (context: any): boolean => !context?.lookAlikeParametersCalculated
    },
    {
      label: "Show look alikes",
      iconClass: "pe-7s-users",
      action: (context: any) => this.showLookAlikes(context),
      checkDisabled: (context: any): boolean => context?.status != 4,
      checkVisible: (context: any): boolean => !!context?.lookAlikeParametersCalculated
    },
    {
      label: "Delete segment",
      iconClass: "pe-7s-trash",
      action: (context: any) => this.deleteSegment(context),
      checkDisabled: (context: any): boolean => context?.status != 4 && context?.status != 5,
    },
    {
      label: "Create audience from seed",
      iconClass: "pe-7s-radio",
      action: (context: any) => this.createAudienceFromSeed(context),
      checkDisabled: (context: any): boolean => context?.status != 4,
    }
  ];

  constructor(    
    private segmentsService: SegmentsService,
    private lookAlikesService: LookAlikesService,
    private router: Router,
    private dialogService: DialogService,
    private audienceParametersDialogServiceService: AudienceParametersDialogService,
    globalsService: GlobalsService,
    usersService: UsersService,
    private audiencesService: AudiencesService,
    private spinnerService: NgxSpinnerService) { 
    super(
      usersService.hasSelectedCompanySectionRight('Segments'),
      globalsService, 
      router);    
  }  

  ngOnInit(): void {
    if(this.redirecting)
      return;
      
    this.activePolling = true;
    this.loadSegments();
  }

  ngOnDestroy(): void {
    this.activePolling = false;
  }

  private loadSegments = (): void => {
    // Show spinner just first time
    if(!this.originalSegments)
      this.spinnerService.show();

    this.segmentsService.getAll().subscribe(
      resp => {
        if (this.activePolling)
        {
          this.originalSegments = resp;
          this.filterSegments(this.segmentsFilter);
          // Next iteration
          this.startRefreshPolling();
          this.spinnerService.hide();
        }
      }, () => {
        this.spinnerService.hide();
      }
    )
  }

  getStatusStyle = (segment: Segment): string => {
    switch (segment?.status) {
      case SegmentStatus.Success:
        return "pe-7s-check";
      case SegmentStatus.Error:
        return "pe-7s-attention";
      default:
        return "pe-7s-hourglass fa-pulse";
    };
  }

  getStatusLabel = (segment: Segment): string => {
    switch (segment?.status) {
      case SegmentStatus.Success:
          return "Done";
      case SegmentStatus.Error:
        return "Error";
      case SegmentStatus.IdentifiersFileCreated:
        return "Identifiers extracted, matching profiles ...";
      case SegmentStatus.CalculatingLookAlikePercentages:
        return "Calculating look alikes parameters ...";
      case SegmentStatus.CalculatingSegmentInsights:
        return "Calculating segment insights ...";
      default:
        return "Extracting identifiers ...";
    };
  }

  viewInsights = (segment: Segment): void => {
    this.router.navigate([ '/segments', segment.id, 'insights' ]);
  }

  calculateLookAlikesParameters = (segment: Segment): void => {
    this.dialogService.showConfirmation(
      `Are you sure to calculate "${segment.fileName}" look alike parameters?`,
      () => {
        this.lookAlikesService.calculateLookAlikesParameters(LookAlikeSourceTypes.Segment, segment.id).subscribe(
          () => { });
      },
      () => { });
  }

  showLookAlikes = (segment: Segment): void => {
    this.router.navigate([ '/segments', segment.id, 'look-alikes' ]);
  }

  deleteSegment = (segment: Segment): void => {
    this.dialogService.showConfirmation(
      `Are you sure to remove "${segment.fileName}"?\n If the segment has related elements (look alikes, audiences, ...)\n  they will be removed too.`,
      () => {
        this.segmentsService.delete(segment.id).subscribe(
          () => {
            let index: number = this.segmentsDataSource.data.findIndex(x => x.id == segment.id)
            this.segmentsDataSource.data.splice(index, 1);
          });
      },
      () => { });
  }

  calculateInsights = (segment: Segment): void => {
    this.dialogService.showConfirmation(
      `Are you sure to ${segment.segmentInsightsCalculated ? 'refresh' : 'calculate'} "${segment.fileName}" insights?`,
      () => {
        this.segmentsService.calculateSegmentInsights(segment.id).subscribe(
          () => { });
      },
      () => { });
  }  

  createAudienceFromSeed = (segment: Segment): void => {
    this.audienceParametersDialogServiceService.show(
      (audienceParameters: AudienceParameters) => {
        // Save audience
        this.spinnerService.show();
        let request: CreateAudienceRequest = {
          name: audienceParameters.name,
          lookAlikeSourceType: LookAlikeSourceTypes.Segment,
          lookAlikeSourceIdentifier: segment.id,
          categoryId: audienceParameters.categoryId
        };

        this.audiencesService.createAudience(request).subscribe(
          () => {
            this.spinnerService.hide();
            this.dialogService.showConfirmation(
              "Audience created, do you want to navigate to audiences list?", 
              () => { this.router.navigate([ '/audiences/list' ]); }, 
              () => {}
            );
          },
          () => {
            this.spinnerService.hide();
          }
        );
      },
      () => { }
    );    
  }  

  filterSegments = (searchTerm: string): void => {
    this.segmentsFilter = searchTerm;
    // We need to build the full object in order to make the eris-table component detect changes
    let cloned: ErisTableDataSource = { ...this.segmentsDataSource};
    cloned.data =         
      !!searchTerm ? 
      this.originalSegments.filter((x: Segment) => { return x.fileName.toUpperCase().indexOf(searchTerm.toUpperCase()) != -1; } ) :
      this.originalSegments;
    this.segmentsDataSource = cloned;
  }  

    stopRefreshPolling = (): void => {
      if (!!this.loadSegmentsSubscription)
        this.loadSegmentsSubscription.unsubscribe();
  
      this.activePolling = false;
  
      console.debug("stopRefreshPolling executed");
    };
  
    startRefreshPolling = (): void => {
      this.stopRefreshPolling();
      this.activePolling = true;
      this.loadSegmentsSubscription = timer(2000).subscribe(() =>
        this.loadSegments()
      );
  
      console.debug("startRefreshPolling executed");
    };
  
    onMenuClosed = (closeReason: MenuCloseReason): void => {
      if (!closeReason) this.startRefreshPolling();
    };
}
